import { Minus, Plus } from "lucide-react";
import { cn } from "@/utils/helpers";

/*
 * Switch (CreativeOS Library.lib.pen, Components / Inputs and selection, style
 * B): a capsule track with a round knob. On is the accent with the knob right;
 * off is the line colour with the knob left. Use it where the change applies at
 * once; inside a form you submit, use a Checkbox.
 */
export function KitSwitch({
  checked,
  onChange,
  label,
  disabled = false,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-pill p-0.5 outline-none [transition:transform_var(--spring-snappy),background-color_160ms] not-disabled:active:scale-[0.97]",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent disabled:cursor-not-allowed disabled:opacity-40",
        checked ? "bg-co-accent-fill" : "bg-co-line2",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "size-4 rounded-full [transition:transform_var(--spring-snappy),background-color_160ms] motion-reduce:transition-none",
          checked ? "translate-x-4 bg-co-on-accent" : "translate-x-0 bg-white shadow-[0_0_3px_#1212122e]",
        )}
      />
    </button>
  );
}

/*
 * Stepper (library, style B): a capsule in control glass with no outer
 * hairline; minus and plus are circles and the value box keeps its dividers.
 * The value is Barlow Condensed 700.
 */
export function KitStepper({
  value,
  label,
  min,
  max,
  onChange,
  disabled = false,
  large = false,
  className,
}: {
  value: number;
  label: string;
  min: number;
  max: number;
  onChange: (n: number) => void;
  disabled?: boolean;
  /** 44px targets, for touch. */
  large?: boolean;
  className?: string;
}) {
  /* Each end is a half capsule, so its hover fills the whole segment up to the
     value box rather than a circle inside it. */
  const button = cn(
    "grid shrink-0 place-items-center text-co-text2 outline-none transition-colors hover:bg-co-hover hover:text-co-text",
    "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent disabled:pointer-events-none disabled:opacity-30",
    large ? "size-11" : "size-8",
  );
  return (
    <span className={cn("co-glass-control inline-flex items-center rounded-pill", className)} role="group" aria-label={label}>
      <button type="button" aria-label={`One fewer, ${label}`} disabled={disabled || value <= min} onClick={() => onChange(value - 1)} className={cn(button, "rounded-s-full")}>
        <Minus className="size-3.5" aria-hidden="true" />
      </button>
      <span
        aria-live="polite"
        className={cn(
          "grid place-items-center border-x border-co-line2 font-section font-bold tabular-nums text-co-text",
          large ? "h-11 w-12 text-[18px]" : "h-8 w-10 text-[16px]",
        )}
      >
        {value}
      </span>
      <button type="button" aria-label={`One more, ${label}`} disabled={disabled || value >= max} onClick={() => onChange(value + 1)} className={cn(button, "rounded-e-full")}>
        <Plus className="size-3.5" aria-hidden="true" />
      </button>
    </span>
  );
}
